/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {SensorUnits} from '@ion/core/src/types/entities';
import {genericUnitConversion} from '@ion/core/src/utils/Conversions';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {SCIDeviceSettingsUnits} from '@ion/dls/src/editors/DeviceEditorUtils';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import {ChartsDataType} from './ChartsTooltip';
import {getTemperatureDisplayUnit} from './utils';

export interface ThresholdAlertProps {
  data: ChartsDataType;
  unit: string;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {label} = getAssetDetailsContentStyles();
  return {
    threshold : {
      fontSize      : 10,
      color         : dlsColor('grey3'),
      paddingBottom : '2px'
    },
    // maps the threshold caption to the shared uppercase label style ...
    currentThreshold : {
      fontFamily    : label.fontFamily,
      fontWeight    : label.fontWeight,
      lineHeight    : label.lineHeight,
      letterSpacing : label.letterSpacing,
      textTransform : label.textTransform,
      paddingBottom : 0
    }
  };
});

/**
 * ThresholdAlert is a component used for displaying threshold data being hovered over in a chart/graph.
 *
 * ```typescript
 *
 * import {ThresholdAlert} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 * <ThresholdAlert threshold={threshold} data={data} />
 *
 * ```
*/
const ThresholdAlert = ({data, unit}: ThresholdAlertProps) => {
  const classes                = useStyles();
  const isCurrent              = isCurrentTheme();
  const celsius                = SensorUnits.C;
  const isTemperature          = unit === SensorUnits.C || unit === SensorUnits.F;
  const isLightLevel           = unit === SCIDeviceSettingsUnits.Light;
  const systemOfUnits          = useSelector(systemOfUnitsSelector);
  const temperatureDisplayUnit = getTemperatureDisplayUnit(unit as SensorUnits, systemOfUnits?.value);
  const {
    settings: {
      maxTemperature = 0,
      minTemperature = 0,
      maxLightLevel = 0,
      minLightLevel = 0
    } = {},
    y: point
  } = data || {};

  const getTemperatureThreshold = () => {
    const {threshold} = data || {};
    const {max, min}  = threshold || {};
    if (max || min) {
      return {
        max : max || genericUnitConversion(maxTemperature, celsius, systemOfUnits),
        min : min || genericUnitConversion(minTemperature, celsius, systemOfUnits)
      };
    }
    return null;
  };

  const getLightLevelThreshold = () => {
    const {threshold} = data || {};
    const {max, min}  = threshold || {};
    if (max || min) {
      return {
        max : max || maxLightLevel,
        min : min || minLightLevel
      };
    }
    return null;
  };

  const getThreshold = () => {
    if (isTemperature) {
      return getTemperatureThreshold();
    } else if (isLightLevel) {
      return getLightLevelThreshold();
    }
    return null;
  };

  const threshold = getThreshold();

  const {min, max} = threshold || {};

  const thresholdValue = (point > max) ? max : (point < min ? min : null);

  if (thresholdValue) {
    return (
      <span className={themedClassName(classes.threshold, classes.currentThreshold, isCurrent)}>
        {translate('tracking:scDeviceChartTooltip.threshold')}:
        &nbsp;{thresholdValue}
        {isTemperature ? '°' : null}
        &nbsp;{isTemperature ? temperatureDisplayUnit : unit}
      </span>
    );
  }
  return null;
};

export default memo(ThresholdAlert);
